import { useEffect, useRef, useState } from "react";
import {
  Box,
  Input,
  FormControl,
  FormLabel,
  Select,
  Button,
  useToast,
  HStack,
  Image,
  Spinner,
  Text,
} from "@chakra-ui/react";
import TipTapEditor from "../common/TipTapEditor";
import axios from "../../axiosConfig";
import { IMAGE_ACCEPT, imageFileError, imageUploadError } from "../common/imageUploadErrors";

const VISIBILITY_OPTIONS = ["public_article", "hub_only", "internal"];
const CATEGORY_OPTIONS = ["resource", "faq", "documentation", "announcement"];

const ArticleForm = ({ initialData = null, onSubmit, isSubmitting }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category: "resource",
    visibility: "public_article",
    image_url: "",
    content: "",
  });

  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // ✅ Initialise once when initialData changes
  useEffect(() => {
    if (initialData) {
      setPreviewUrl(initialData.image_url || "");
      setFormData({
        title: initialData.title || "",
        slug: initialData.slug || "",
        category: initialData.category || "resource",
        visibility: initialData.visibility || "public_article",
        image_url: initialData.image_reference ?? initialData.image_url ?? "",
        content: initialData.content || "",
      });
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --------------------------------------------------
  // Cover image upload
  // --------------------------------------------------
  const uploadCoverImage = async (file) => {
    const validationError = imageFileError(file);
    if (validationError) {
      toast({ title: "Image upload failed", description: validationError, status: "error", isClosable: true });
      return;
    }
    setIsUploading(true);
    const form = new FormData();
    form.append("file", file);

    try {
      const response = await axios.post("/uploads/articles/cover", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Persist only the durable reference. url is a short-lived preview credential.
      // Fallback supports deploying Face before Brain (old Brain returns durable URLs).
      const reference = response.data.reference ?? response.data.url;
      setFormData((prev) => ({ ...prev, image_url: reference }));
      setPreviewUrl(response.data.url);
      // thumb_reference/thumb_url are an optional preview variant, not the saved cover.

      toast({
        title: "Image uploaded",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Image upload failed",
        description: imageUploadError(err),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadCoverImage(file);
  };

  // --------------------------------------------------
  // Submit
  // --------------------------------------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.replace(/<[^>]+>/g, "").trim()) {
      toast({
        title: "Missing required fields",
        description: "Title and content are required",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    onSubmit(formData);
  };

  return (
    <Box maxW="800px" mx="auto">
      <form onSubmit={handleSubmit}>
        {/* Title */}
        <FormControl mb={4} isRequired>
          <FormLabel>Title</FormLabel>
          <Input
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
          />
        </FormControl>

        {/* Slug */}
        <FormControl mb={4}>
          <FormLabel>Slug</FormLabel>
          <Input
            value={formData.slug}
            onChange={(e) => handleChange("slug", e.target.value)}
          />
        </FormControl>

        {/* Category */}
        <FormControl mb={4}>
          <FormLabel>Category</FormLabel>
          <Select
            value={formData.category}
            onChange={(e) => handleChange("category", e.target.value)}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* Visibility */}
        <FormControl mb={4}>
          <FormLabel>Visibility</FormLabel>
          <Select
            value={formData.visibility}
            onChange={(e) => handleChange("visibility", e.target.value)}
          >
            {VISIBILITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.replace("_", " ")}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* Cover Image */}
        <FormControl mb={6}>
          <FormLabel>Cover Image</FormLabel>
          <Box
            border="2px dashed #ccc"
            borderRadius="md"
            p={4}
            textAlign="center"
            cursor="pointer"
            onClick={() => fileInputRef.current.click()}
          >
            {isUploading ? (
              <HStack justify="center">
                <Text>Uploading image...</Text>
                <Spinner size="sm" />
              </HStack>
            ) : previewUrl ? (
              <Image alt="Cover preview" src={previewUrl} maxH="200px" mx="auto" />
            ) : (
              <Text>Click to upload cover image</Text>
            )}

            <Input
              type="file"
              accept={IMAGE_ACCEPT}
              aria-label="Cover image"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              display="none"
            />
          </Box>
        </FormControl>

        {/* Content */}
        <FormControl mb={6}>
          <FormLabel>Content</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
            <TipTapEditor
              key="article-content-editor"
              content={formData.content}
              onChange={(value) => handleChange("content", value)}
            />
          </Box>
        </FormControl>

        {/* Submit */}
        <Button type="submit" colorScheme="blue" isLoading={isSubmitting} isDisabled={isUploading}>
          Save Article
        </Button>
      </form>
    </Box>
  );
};

export default ArticleForm;
